import {useMemo} from "react";

import {useQueries} from "@tanstack/react-query";
import every from "lodash/every";
import find from "lodash/find";
import some from "lodash/some";
import {useSearchParams} from "react-router-dom";

import useMenuDates from "@globals/hooks/useMenuDates";
import {LunchSearchParams} from "@globals/interfaces/LunchFilters";
import {
  DateString,
  ObjectWithAnyProps,
  ResponseError,
} from "@globals/types/utilityTypes";
import {useUser} from "@globals/user";
import {checkValidDateStr} from "@globals/utils/toDateString";

import {fetchBrandMenu, handleError} from "../BrandService";
import {BrandMenuResource} from "../interfaces";

export default function useBrandMenu(
  operatorSlug?: string,
  restaurantSlug?: string
): BrandMenuResource {
  const {user, isAuthorized} = useUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedLocationId = useMemo<number | undefined>(() => {
    return user?.selected_location.id;
  }, [user]);

  const {
    menuDates,
    closedDates,
    isLoading: isMenuDatesLoading,
    isError: isMenuDatesError,
    error: menuDatesError,
  } = useMenuDates({
    locationId: selectedLocationId,
    disabled: !isAuthorized && !selectedLocationId,
  });

  const availableDays = useMemo(() => {
    return menuDates.filter((menuDate) => !closedDates.includes(menuDate));
  }, [menuDates, closedDates]);

  const queriesAvailableDays = availableDays.map((menuDate) => {
    return {
      queryKey: ["brandMenu", menuDate],
      queryFn: () => fetchBrandMenu(operatorSlug!, restaurantSlug!, menuDate),
      enabled: isAuthorized && Boolean(operatorSlug && restaurantSlug),
    };
  });

  const results = useQueries({
    queries: queriesAvailableDays,
  });

  const brandMenus = useMemo(() => {
    return results.flatMap(({data}) => {
      if (data && Object.values(data.items).length) {
        return data;
      }
      // returning empty array means 'skip'
      return [];
    });
  }, [results]);

  const isError = some(results, "isError");
  const error = find(results, {isError: true})?.error as ResponseError;
  const isLoading = some(results, "isPending");
  const isFetched = every(results, "isFetched");
  const hasNoUpcomingDishes =
    !isLoading && isFetched && !isMenuDatesLoading && brandMenus.length === 0;

  const dailyMenuDate = useMemo<DateString>(() => {
    const date = searchParams.get(LunchSearchParams.Date) as DateString;
    const isValidDateParam = checkValidDateStr(date || "");
    if (isValidDateParam) return date;
    return menuDates[0];
  }, [searchParams, menuDates]);

  const setNextMenuDate = (nextDate: number) => {
    const correspondingDate = menuDates[Number(nextDate) - 1];
    const nextDateSearchParams = new URLSearchParams(searchParams);
    nextDateSearchParams.set(LunchSearchParams.Date, correspondingDate);
    setSearchParams(nextDateSearchParams);
  };

  return {
    brandMenus,
    menuDates,
    dailyMenuDate,
    hasNoUpcomingDishes,
    isLoading: !isFetched || isLoading || isMenuDatesLoading,
    isError: isMenuDatesError || isError,
    error: isMenuDatesError
      ? handleError(menuDatesError as ObjectWithAnyProps, "upcoming menus")
      : error,
    setNextMenuDate,
  };
}
